import { useId } from "react";
import { AuthImage } from "@/components/AuthImage";

const PALETTES = {
  lips: ["#B5636A", "#C47F7A", "#9E4F5C", "#D29A92", "#A8545E", "#C9787E"],
  eyeliner: ["#1F1A19", "#2B2321", "#26302A"],
  correction: ["#C9853E", "#D2A54A", "#B86B3C", "#7E8C4E"],
  areola: ["#C99A86", "#B98571", "#D8B4A0"],
  scar: ["#D9B9A2", "#C9A288", "#E1C7B2"],
  brows: ["#7A4A2E", "#8A5A3C", "#5E3B28", "#6E4530", "#9C6B4C", "#4A2F22", "#A7785A"],
};
const KEYWORDS = [
  [/black|onyx|ebony|noir/i, "#231C1A"], [/blonde|honey|sand|light tan/i, "#A87A4F"], [/red|cherry|crimson|apple/i, "#A63E3E"],
  [/pink|rose|blush|berry/i, "#C2727A"], [/coral|peach|apricot/i, "#D98A70"], [/orange|copper|ginger|papaya|pumpkin/i, "#C47A3A"],
  [/yellow|gold|lemon|limoncello/i, "#D0A64A"], [/green|olive|lime/i, "#7A8450"], [/grey|gray|ash|taupe/i, "#857469"],
  [/white/i, "#EDE6DC"], [/mauve|plum|wine|maroon|fig/i, "#7E4752"],
];

const hash = (s) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

export function bottleColor(item) {
  const kw = KEYWORDS.find(([re]) => re.test(item.shade || ""));
  if (kw) return kw[1];
  const use = item.pigment?.uses?.[0] || (item.pigment?.area === "lips" ? "lips" : "brows");
  const pal = PALETTES[use] || PALETTES.brows;
  return pal[hash(`${item.brand}${item.line}${item.shade}`) % pal.length];
}

const RIBS = Array.from({ length: 13 }, (_, i) => 30.5 + i * 3.25);
const BODY = "M33 70h34c3 0 5 1 7 3l3 4c2 3 3 6 3 10v52c0 5-3 8-8 8H28c-5 0-8-3-8-8V87c0-4 1-7 3-10l3-4c2-2 4-3 7-3z";

function BottleSvg({ color }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 100 150" className="absolute inset-0 h-full w-full drop-shadow-[0_8px_10px_rgba(44,24,16,0.22)]" aria-hidden="true">
      <defs>
        <linearGradient id={`cap${id}`} x1="0" x2="1">
          <stop offset="0" stopColor="#2c2828" /><stop offset=".25" stopColor="#0c0a0a" /><stop offset=".6" stopColor="#1d1a1a" /><stop offset="1" stopColor="#050404" />
        </linearGradient>
        <linearGradient id={`shade${id}`} x1="0" x2="1">
          <stop offset="0" stopColor="#000" stopOpacity=".22" /><stop offset=".18" stopColor="#fff" stopOpacity=".26" />
          <stop offset=".34" stopColor="#fff" stopOpacity=".03" /><stop offset=".75" stopColor="#000" stopOpacity=".05" /><stop offset="1" stopColor="#000" stopOpacity=".35" />
        </linearGradient>
        <linearGradient id={`label${id}`} x1="0" x2="1">
          <stop offset="0" stopColor="#E6DDD0" /><stop offset=".22" stopColor="#F8F3EA" /><stop offset=".78" stopColor="#F4EEE4" /><stop offset="1" stopColor="#DCD2C4" />
        </linearGradient>
        <linearGradient id={`gold${id}`} x1="0" x2="1">
          <stop offset="0" stopColor="#A9834A" /><stop offset=".45" stopColor="#EAD5A4" /><stop offset="1" stopColor="#A07940" />
        </linearGradient>
      </defs>
      <path d="M41 2.5c0-.8.7-1.5 1.5-1.5h15c.8 0 1.5.7 1.5 1.5L60 17H40z" fill={`url(#cap${id})`} />
      <path d="M38.5 17h23l8.5 20H30z" fill={`url(#cap${id})`} />
      <path d="M44 3h2.4l-1.6 33h-3.4z" fill="#fff" opacity=".12" />
      <rect x="28" y="37" width="44" height="21" rx="1.5" fill={`url(#cap${id})`} />
      {RIBS.map((x) => <rect key={x} x={x} y="38.5" width="1" height="18" fill="#000" opacity=".75" />)}
      {RIBS.map((x) => <rect key={`h${x}`} x={x + 1.3} y="38.5" width=".5" height="18" fill="#fff" opacity=".08" />)}
      <rect x="25.5" y="57.5" width="49" height="5.5" rx="2" fill={`url(#cap${id})`} />
      <rect x="28" y="63.5" width="44" height="6.5" rx="1.5" fill={`url(#cap${id})`} />
      <rect x="26" y="58" width="48" height=".7" fill="#fff" opacity=".14" />
      <path d={BODY} fill={color} />
      <path d={BODY} fill={`url(#shade${id})`} />
      <path d="M27 77c2-3 5-4 9-4h3c-5 1-8 4-10 8-1 2-2 5-2 7H22c0-4 2-8 5-11z" fill="#fff" opacity=".28" />
      <rect x="20" y="88" width="60" height="41" fill={`url(#label${id})`} />
      <rect x="20" y="87" width="60" height="1.3" fill={`url(#gold${id})`} />
      <rect x="20" y="128.7" width="60" height="1.3" fill={`url(#gold${id})`} />
      <rect x="20" y="88" width="60" height="41" fill={`url(#shade${id})`} opacity=".3" />
    </svg>
  );
}

export function PigmentBottle({ item, testId, compact = false }) {
  const fid = item.image_file_id || item.pigment?.image_file_id;
  const label = item.shade || item.line || "Pigment";
  if (fid) {
    return (
      <div className="relative aspect-[2/3] w-full overflow-hidden" data-testid={testId}>
        <div className="absolute inset-0 overflow-hidden rounded-2xl bg-[#FDFBF7] shadow-[0_8px_18px_rgba(44,24,16,0.16)] ring-1 ring-[#E6DBCF]">
          <AuthImage id={fid} alt={`${item.brand} ${label} bottle`} className="h-full w-full object-contain" testId={testId ? `${testId}-photo` : undefined} />
        </div>
      </div>
    );
  }
  return (
    <div className="relative aspect-[2/3] w-full" data-testid={testId} title={`${item.brand} · ${label}`}>
      <BottleSvg color={bottleColor(item)} />
      <div lang="en" className="absolute inset-x-[22%] top-[59%] flex h-[26%] flex-col items-center justify-center overflow-hidden text-center">
        {!compact && <span className="block w-full truncate text-[7px] font-semibold uppercase tracking-[0.14em] text-[#8A7466]">{item.brand}</span>}
        <span className={`line-clamp-2 w-full font-serif font-semibold leading-[1.08] text-[#2C1810] [hyphens:auto] [word-break:normal] ${compact ? "text-[9px]" : label.length > 14 ? "text-[10.5px]" : "text-[12px]"}`}>{label}</span>
      </div>
    </div>
  );
}
